import { forwardRef, useState } from "react";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Calendar, X } from "lucide-react";
import { BaseComponentProps } from "@/shared/ui/base";

export interface DatePickerProps extends BaseComponentProps {
  label?: string;
  error?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  selected: Date | null;
  onChange: (date: Date | null) => void;
  minDate?: Date;
  maxDate?: Date;
  placeholderText?: string;
  compact?: boolean;
}

export const DatePicker = forwardRef<HTMLDivElement, DatePickerProps>(
  (
    {
      label,
      error,
      helperText,
      required,
      disabled,
      selected,
      onChange,
      minDate,
      maxDate,
      placeholderText,
      compact = false,
      className,
      "data-testid": dataTestId,
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = useState(false);

    const borderClass = error
      ? "border-red-500"
      : isFocused
        ? "border-red-500"
        : "border-gray-300 hover:border-gray-400";

    const paddingClass = compact ? "py-2" : "py-3";

    return (
      <div ref={ref} className={className} data-testid={dataTestId}>
        {label && (
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
            <Calendar className="w-5 h-5 text-gray-400" />
          </div>
          <ReactDatePicker
            selected={selected}
            onChange={onChange}
            minDate={minDate}
            maxDate={maxDate}
            placeholderText={placeholderText}
            dateFormat="MMM d, yyyy"
            disabled={disabled}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            className={`w-full pl-10 ${selected ? "pr-10" : "pr-4"} ${paddingClass} border rounded-lg cursor-pointer outline-none transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed ${borderClass}`}
            calendarClassName="apaato-calendar"
          />
          {selected && !disabled && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange(null);
              }}
              className="absolute inset-y-0 right-0 pr-2 flex items-center z-10 transition-colors"
              aria-label="Clear date"
            >
              <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
            </button>
          )}
        </div>

        {/* Helper Text or Error */}
        {(helperText || error) && (
          <p
            className={`mt-1 text-sm ${error ? "text-red-500" : "text-gray-500"}`}
          >
            {error || helperText}
          </p>
        )}
      </div>
    );
  },
);

DatePicker.displayName = "DatePicker";
